import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Chip,
  Alert,
  Snackbar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from '@mui/material';
import {
  Save as SaveIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Security as SecurityIcon,
  Notifications as NotificationsIcon,
  Storage as StorageIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

interface SystemSettings {
  maintenanceMode: boolean;
  maxSessionDuration: number;
  dataRetentionDays: number;
  autoBackupEnabled: boolean;
  debugLogging: boolean;
  maxFileUploadSize: number;
  allowGuestAccess: boolean;
}

interface NotificationSettings {
  emailNotifications: boolean;
  contractExpiryAlerts: boolean;
  invoiceReminders: boolean;
  systemMaintenanceAlerts: boolean;
  alertEmail: string;
  notificationFrequency: 'immediate' | 'daily' | 'weekly';
}

interface SecuritySettings {
  passwordMinLength: number;
  requireMFA: boolean;
  sessionTimeout: number;
  maxFailedLogins: number;
  ipWhitelist: string[];
  auditLogRetentionDays: number;
}

interface ApiRateLimit {
  endpoint: string;
  requestsPerMinute: number;
  burstLimit: number;
}

export const SettingsPage: React.FC = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  // System Settings State
  const [systemSettings] = useState<SystemSettings>({
    maintenanceMode: false,
    maxSessionDuration: 8,
    dataRetentionDays: 365,
    autoBackupEnabled: true,
    debugLogging: false,
    maxFileUploadSize: 10,
    allowGuestAccess: false,
  });

  // Notification Settings State
  const [notificationSettings] = useState<NotificationSettings>({
    emailNotifications: true,
    contractExpiryAlerts: true,
    invoiceReminders: true,
    systemMaintenanceAlerts: true,
    alertEmail: 'admin@ets-navigator.com',
    notificationFrequency: 'daily',
  });

  // Security Settings State
  const [securitySettings] = useState<SecuritySettings>({
    passwordMinLength: 8,
    requireMFA: false,
    sessionTimeout: 30,
    maxFailedLogins: 5,
    ipWhitelist: ['192.168.1.100', '10.0.0.50', '172.16.0.1'],
    auditLogRetentionDays: 90,
  });

  // API Rate Limits State
  const [apiRateLimits] = useState<ApiRateLimit[]>([
    { endpoint: '/api/v1/users', requestsPerMinute: 100, burstLimit: 200 },
    { endpoint: '/api/v1/vessels', requestsPerMinute: 200, burstLimit: 400 },
    { endpoint: '/api/v1/contracts', requestsPerMinute: 150, burstLimit: 300 },
  ]);

  const [newIpAddress] = useState('192.168.1.1');

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Settings
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          Configure system-wide settings and preferences for the ETS Navigator admin panel
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Preview Mode:</strong> These settings are currently for demonstration purposes
            only. All controls are disabled to show the planned configuration interface.
          </Typography>
        </Alert>
      </Box>

      {/* Settings Tabs */}
      <Paper sx={{ flex: 1 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          aria-label="settings tabs"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            icon={<SettingsIcon />}
            iconPosition="start"
            label="System"
            id="settings-tab-0"
            aria-controls="settings-tabpanel-0"
          />
          <Tab
            icon={<NotificationsIcon />}
            iconPosition="start"
            label="Notifications"
            id="settings-tab-1"
            aria-controls="settings-tabpanel-1"
          />
          <Tab
            icon={<SecurityIcon />}
            iconPosition="start"
            label="Security"
            id="settings-tab-2"
            aria-controls="settings-tabpanel-2"
          />
          <Tab
            icon={<StorageIcon />}
            iconPosition="start"
            label="API & Performance"
            id="settings-tab-3"
            aria-controls="settings-tabpanel-3"
          />
        </Tabs>

        {/* System Settings Tab */}
        <TabPanel value={currentTab} index={0}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card
                sx={{
                  height: '100%',
                  border: '1px solid',
                  borderColor: 'grey.200',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  '&:hover': {
                    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                  },
                }}
              >
                <CardHeader
                  title="System Configuration"
                  sx={{
                    backgroundColor: 'grey.50',
                    borderBottom: '1px solid',
                    borderColor: 'grey.200',
                  }}
                />
                <CardContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <FormControlLabel
                      control={<Switch checked={systemSettings.maintenanceMode} disabled />}
                      label="Maintenance Mode"
                    />

                    <TextField
                      label="Max Session Duration (hours)"
                      type="number"
                      value={systemSettings.maxSessionDuration}
                      disabled
                      fullWidth
                    />

                    <TextField
                      label="Data Retention (days)"
                      type="number"
                      value={systemSettings.dataRetentionDays}
                      disabled
                      fullWidth
                    />

                    <TextField
                      label="Max File Upload Size (MB)"
                      type="number"
                      value={systemSettings.maxFileUploadSize}
                      disabled
                      fullWidth
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Card
                sx={{
                  height: '100%',
                  border: '1px solid',
                  borderColor: 'grey.200',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  '&:hover': {
                    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                  },
                }}
              >
                <CardHeader
                  title="System Features"
                  sx={{
                    backgroundColor: 'grey.50',
                    borderBottom: '1px solid',
                    borderColor: 'grey.200',
                  }}
                />
                <CardContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormControlLabel
                      control={<Switch checked={systemSettings.autoBackupEnabled} disabled />}
                      label="Auto Backup Enabled"
                    />

                    <FormControlLabel
                      control={<Switch checked={systemSettings.debugLogging} disabled />}
                      label="Debug Logging"
                    />

                    <FormControlLabel
                      control={<Switch checked={systemSettings.allowGuestAccess} disabled />}
                      label="Allow Guest Access"
                    />

                    <Button variant="contained" startIcon={<SaveIcon />} disabled sx={{ mt: 2 }}>
                      Save System Settings
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Notifications Settings Tab */}
        <TabPanel value={currentTab} index={1}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 8 }}>
              <Card
                sx={{
                  border: '1px solid',
                  borderColor: 'grey.200',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  '&:hover': {
                    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                  },
                }}
              >
                <CardHeader
                  title="Notification Preferences"
                  sx={{
                    backgroundColor: 'grey.50',
                    borderBottom: '1px solid',
                    borderColor: 'grey.200',
                  }}
                />
                <CardContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <FormControlLabel
                      control={
                        <Switch checked={notificationSettings.emailNotifications} disabled />
                      }
                      label="Email Notifications"
                    />

                    <FormControlLabel
                      control={
                        <Switch checked={notificationSettings.contractExpiryAlerts} disabled />
                      }
                      label="Contract Expiry Alerts"
                    />

                    <FormControlLabel
                      control={<Switch checked={notificationSettings.invoiceReminders} disabled />}
                      label="Invoice Reminders"
                    />

                    <FormControlLabel
                      control={
                        <Switch checked={notificationSettings.systemMaintenanceAlerts} disabled />
                      }
                      label="System Maintenance Alerts"
                    />

                    <TextField
                      label="Alert Email Address"
                      type="email"
                      value={notificationSettings.alertEmail}
                      disabled
                      fullWidth
                    />

                    <FormControl fullWidth disabled>
                      <InputLabel>Notification Frequency</InputLabel>
                      <Select
                        value={notificationSettings.notificationFrequency}
                        label="Notification Frequency"
                      >
                        <MenuItem value="immediate">Immediate</MenuItem>
                        <MenuItem value="daily">Daily Digest</MenuItem>
                        <MenuItem value="weekly">Weekly Summary</MenuItem>
                      </Select>
                    </FormControl>

                    <Button variant="contained" startIcon={<SaveIcon />} disabled sx={{ mt: 2 }}>
                      Save Notification Settings
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Security Settings Tab */}
        <TabPanel value={currentTab} index={2}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card
                sx={{
                  height: '100%',
                  border: '1px solid',
                  borderColor: 'grey.200',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  '&:hover': {
                    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                  },
                }}
              >
                <CardHeader
                  title="Authentication & Access"
                  sx={{
                    backgroundColor: 'grey.50',
                    borderBottom: '1px solid',
                    borderColor: 'grey.200',
                  }}
                />
                <CardContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <TextField
                      label="Minimum Password Length"
                      type="number"
                      value={securitySettings.passwordMinLength}
                      disabled
                      fullWidth
                    />

                    <FormControlLabel
                      control={<Switch checked={securitySettings.requireMFA} disabled />}
                      label="Require Multi-Factor Authentication"
                    />

                    <TextField
                      label="Session Timeout (minutes)"
                      type="number"
                      value={securitySettings.sessionTimeout}
                      disabled
                      fullWidth
                    />

                    <TextField
                      label="Max Failed Login Attempts"
                      type="number"
                      value={securitySettings.maxFailedLogins}
                      disabled
                      fullWidth
                    />

                    <TextField
                      label="Audit Log Retention (days)"
                      type="number"
                      value={securitySettings.auditLogRetentionDays}
                      disabled
                      fullWidth
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Card
                sx={{
                  height: '100%',
                  border: '1px solid',
                  borderColor: 'grey.200',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  '&:hover': {
                    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                  },
                }}
              >
                <CardHeader
                  title="IP Whitelist"
                  sx={{
                    backgroundColor: 'grey.50',
                    borderBottom: '1px solid',
                    borderColor: 'grey.200',
                  }}
                />
                <CardContent>
                  <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                    <TextField
                      label="IP Address"
                      value={newIpAddress}
                      placeholder="192.168.1.1"
                      size="small"
                      disabled
                      sx={{ flex: 1 }}
                    />
                    <Button variant="outlined" startIcon={<AddIcon />} disabled>
                      Add
                    </Button>
                  </Box>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                    {securitySettings.ipWhitelist.map((ip) => (
                      <Chip
                        key={ip}
                        label={ip}
                        color="primary"
                        variant="filled"
                        disabled
                        sx={{
                          backgroundColor: 'primary.50',
                          color: 'primary.700',
                          border: '1px solid',
                          borderColor: 'primary.200',
                          fontFamily: 'monospace',
                          fontWeight: 500,
                        }}
                      />
                    ))}
                  </Box>

                  <Button variant="contained" startIcon={<SaveIcon />} disabled>
                    Save Security Settings
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* API & Performance Settings Tab */}
        <TabPanel value={currentTab} index={3}>
          <Card
            sx={{
              border: '1px solid',
              borderColor: 'grey.200',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              '&:hover': {
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
              },
            }}
          >
            <CardHeader
              title="API Rate Limits"
              sx={{
                backgroundColor: 'grey.50',
                borderBottom: '1px solid',
                borderColor: 'grey.200',
              }}
            />
            <CardContent>
              <TableContainer
                sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: 1 }}
              >
                <Table>
                  <TableHead sx={{ backgroundColor: 'grey.50' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Endpoint</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Requests/Minute</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Burst Limit</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {apiRateLimits.map((limit) => (
                      <TableRow
                        key={limit.endpoint}
                        sx={{ '&:hover': { backgroundColor: 'grey.50' } }}
                      >
                        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                          {limit.endpoint}
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            value={limit.requestsPerMinute}
                            size="small"
                            disabled
                            sx={{ width: 100 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            value={limit.burstLimit}
                            size="small"
                            disabled
                            sx={{ width: 100 }}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton color="error" disabled>
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ mt: 3 }}>
                <Button variant="contained" startIcon={<SaveIcon />} disabled>
                  Save API Settings
                </Button>
              </Box>
            </CardContent>
          </Card>
        </TabPanel>
      </Paper>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
